import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import CurrentConditions from "@/components/CurrentConditions";
import ForecastSection from "@/components/ForecastSection";
import DetailedData from "@/components/DetailedData";
import NearbySpots from "@/components/NearbySpots";
import SurfSpotStats from "@/components/SurfSpotStats";
import LoadingScreen from "@/components/LoadingScreen";
import SpotMap from "@/components/SpotMap";
import LiveImages from "@/components/LiveImages";

import Footer from "@/components/Footer";
import { Location } from "@/types/weather";



type ActiveTab = "conditions" | "live-images" | "map";

export default function Home() {
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true); // Start as true
  const [isNavigating, setIsNavigating] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("conditions");
  const [location] = useLocation();
  const [urlParams, setUrlParams] = useState(new URLSearchParams(window.location.search));

  // Get location name from URL parameters
  const getLocationNameFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('location');
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser");
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(`/api/locations/nearby?lat=${latitude}&lng=${longitude}`);
          if (response.ok) {
            const location = await response.json();
            setCurrentLocation(location);
          }
        } catch (error) {
          console.error("Error fetching nearby location:", error);
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setIsLoadingLocation(false);
      }
    );
  };

  // Monitor URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const newParams = new URLSearchParams(window.location.search);
      setUrlParams(newParams);
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Load location based on URL parameter or default
  useEffect(() => {
    const loadLocation = async () => {
      const locationParam = urlParams.get('location');
      
      console.log('Loading location from URL:', locationParam, 'Current location:', currentLocation?.name);
      
      // Start loading state when navigating
      if (locationParam && currentLocation) {
        const locationId = parseInt(locationParam);
        if (!isNaN(locationId) && currentLocation.id !== locationId) {
          setIsNavigating(true);
          // Scroll to top for better UX
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      
      if (locationParam) {
        // Check if it's a numeric ID
        const locationId = parseInt(locationParam);
        if (!isNaN(locationId)) {
          // Skip if we already have this location loaded
          if (currentLocation && currentLocation.id === locationId) {
            console.log('Location already loaded:', currentLocation.name);
            setIsLoadingLocation(false);
            setIsNavigating(false);
            return;
          }
          
          // Load specific location by ID
          try {
            const response = await fetch(`/api/locations/all`);
            if (response.ok) {
              const locations = await response.json();
              const location = locations.find((loc: Location) => loc.id === locationId);
              if (location) {
                console.log('Found location by ID:', location.name);
                setCurrentLocation(location);
                setIsLoadingLocation(false);
                setIsNavigating(false);
                return;
              }
            }
          } catch (error) {
            console.error("Error loading location by ID:", error);
            setIsLoadingLocation(false);
            setIsNavigating(false);
          }
        } else {
          // Load specific location by name (fallback for old URLs)
          try {
            const response = await fetch(`/api/locations/search?q=${encodeURIComponent(locationParam)}`);
            if (response.ok) {
              const locations = await response.json();
              if (locations.length > 0) {
                console.log('Found location by name:', locations[0].name);
                setCurrentLocation(locations[0]);
                setIsLoadingLocation(false);
                setIsNavigating(false);
                return;
              }
            }
          } catch (error) {
            console.error("Error loading location by name:", error);
            setIsLoadingLocation(false);
            setIsNavigating(false);
          }
        }
      }
      
      // Fallback to default location only if no current location
      if (!currentLocation) {
        try {
          const response = await fetch("/api/locations/search?q=Malibu");
          if (response.ok) {
            const locations = await response.json();
            if (locations.length > 0) {
              console.log('Loading default location:', locations[0].name);
              setCurrentLocation(locations[0]);
              setIsLoadingLocation(false);
            }
          }
        } catch (error) {
          console.error("Error loading default location:", error);
          setIsLoadingLocation(false);
        }
      }
    };

    loadLocation();
  }, [location, urlParams]);

  // Show full loading screen when initially loading or navigating
  if (isLoadingLocation || isNavigating) {
    return (
      <div className="ls-shell min-h-screen">
        <Header onLocationSelect={setCurrentLocation} />
        <LoadingScreen type="conditions" />
        <Footer />
      </div>
    );
  }

  return (
    <div className="ls-shell min-h-screen">
      <Header onLocationSelect={setCurrentLocation} />
      
      {currentLocation ? (
        <div className="w-full">
          {/* ── Tab bar ───────────────────────────────────────────────── */}
          <div className="max-w-[1180px] mx-auto px-4 pt-5 pb-2">
            <div
              role="tablist"
              aria-label="Location views"
              className="flex gap-1 p-1 rounded-xl"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
            >
              {(["conditions", "live-images", "map"] as ActiveTab[]).map((tab, index) => (
                <button
                  key={tab}
                  id={`location-tab-${tab}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  aria-controls={`location-panel-${tab}`}
                  tabIndex={activeTab === tab ? 0 : -1}
                  onClick={() => setActiveTab(tab)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                      event.preventDefault();
                      const tabs: ActiveTab[] = ["conditions", "live-images", "map"];
                      const direction = event.key === "ArrowRight" ? 1 : -1;
                      const next = tabs[(index + direction + tabs.length) % tabs.length];
                      setActiveTab(next);
                      document.getElementById(`location-tab-${next}`)?.focus();
                    } else if (event.key === "Home" || event.key === "End") {
                      event.preventDefault();
                      const next: ActiveTab = event.key === "Home" ? "conditions" : "map";
                      setActiveTab(next);
                      document.getElementById(`location-tab-${next}`)?.focus();
                    }
                  }}
                  className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all capitalize ${
                    activeTab === tab
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                      : "text-slate-500 hover:text-slate-300 border border-transparent"
                  }`}
                >
                  {tab === "conditions" ? "Conditions" : tab === "live-images" ? "Live Images" : "Map"}
                </button>
              ))}
            </div>
          </div>

          {/* ── Tab content ───────────────────────────────────────────── */}
          {activeTab === "conditions" ? (
            <div id="location-panel-conditions" role="tabpanel" aria-labelledby="location-tab-conditions" tabIndex={0} className="ls-desktop-grid">
              <div><CurrentConditions location={currentLocation} /></div>
              <div className="min-w-0"><ForecastSection location={currentLocation} /><DetailedData location={currentLocation} /></div>
              <div className="lg:col-span-2"><NearbySpots location={currentLocation} /></div>
            </div>
          ) : activeTab === "live-images" ? (
            <div id="location-panel-live-images" role="tabpanel" aria-labelledby="location-tab-live-images" tabIndex={0}>
              <LiveImages key={currentLocation.id} location={currentLocation} />
            </div>
          ) : (
            <div id="location-panel-map" role="tabpanel" aria-labelledby="location-tab-map" tabIndex={0} className="ls-reading-width"><SpotMap
              lat={currentLocation.latitude}
              lon={currentLocation.longitude}
              name={currentLocation.name}
            /></div>
          )}
        </div>
      ) : (
        <div className="container mx-auto px-4 py-12 space-y-8 max-w-7xl">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-blue-900 dark:text-white mb-4">
              Welcome to LiveSwell
            </h2>
            <p className="text-blue-900 dark:text-emerald-400 mb-8">
              Search for a coastal location or allow location access to get started
            </p>
          </div>
          <SurfSpotStats />
        </div>
      )}
      
      <Footer />
    </div>
  );
}
